import React,{createContext,useContext,useReducer,useEffect,useState,useRef} from 'react';
import {v4 as uuidv4} from 'uuid';
import {useAuth} from './AuthContext';
import * as dataService from '../dataService.js';
import {QUEUE_SCHEMA_VERSION,getCachedLibrary,saveCachedLibrary,queueMutation,getQueuedMutations,getPendingMutationCount,removeQueuedMutation,updateQueuedMutation,cancelQueuedEntity,remapQueuedEntityId} from '../utils/database.js';

const BookmarkContext=createContext();
const BACKGROUND_REFRESH_MIN_INTERVAL_MS=60000;

export const useBookmarks=()=> {
  const context=useContext(BookmarkContext);
  if (!context) throw new Error('useBookmarks must be used within a BookmarkProvider');
  return context;
};

const normaliseFolderId=(folderId)=> folderId || null;
const isTemporaryId=(id)=> String(id || '').startsWith('temp-');
const nowDate=()=> new Date();

const normaliseUrlKey=(value)=> {
  try {
    const url=new URL(String(value || '').trim());
    url.hash='';
    url.hostname=url.hostname.toLowerCase().replace(/^www\./,'');
    if (url.pathname==='/' || url.pathname==='') url.pathname='';
    else url.pathname=url.pathname.replace(/\/$/,'');
    const sorted=[...url.searchParams.entries()].sort(([a],[b])=> a.localeCompare(b));
    url.search='';
    sorted.forEach(([key,val])=> url.searchParams.append(key,val));
    return `${url.hostname}${url.pathname}${url.search}`.toLowerCase();
  } catch {return String(value || '').trim().toLowerCase();}
};

const replaceRecordId=(records,oldId,nextRecord)=> {
  const oldIndex=records.findIndex(record=> record.id===oldId);
  const without=records.filter(record=> record.id!==oldId && record.id!==nextRecord.id);
  const index=oldIndex===-1 ? without.length : Math.max(0,Math.min(oldIndex,without.length));
  const next=[...without];
  next.splice(index,0,nextRecord);
  return next;
};

const moveBookmarkInList=(bookmarks,id,folderId,destinationIndex)=> {
  const existingIndex=bookmarks.findIndex(bookmark=> bookmark.id===id);
  if (existingIndex===-1) return bookmarks;
  const existing=bookmarks[existingIndex];
  const remaining=bookmarks.filter(bookmark=> bookmark.id!==id);
  const targetFolderId=normaliseFolderId(folderId);
  const targetItems=remaining.filter(bookmark=> normaliseFolderId(bookmark.folderId)===targetFolderId);
  const bounded=Math.max(0,Math.min(Number(destinationIndex) || 0,targetItems.length));
  let insertionIndex=remaining.length;
  if (targetItems.length>0) {
    if (bounded<targetItems.length) insertionIndex=remaining.findIndex(bookmark=> bookmark.id===targetItems[bounded].id);
    else insertionIndex=remaining.findIndex(bookmark=> bookmark.id===targetItems[targetItems.length-1].id)+1;
  }
  const moved={...existing,folderId:targetFolderId,sortOrder:bounded,dateModified:nowDate(),_pending:true};
  const next=[...remaining];
  next.splice(insertionIndex,0,moved);
  return next;
};

const initialState={bookmarks:[],folders:[],selectedItems:[],selectedFolders:[],searchQuery:'',searchResults:[],searchScope:'current',activeFolderId:'ALL',isLoading:false,error:null,undoStack:[],bookmarkCounts:{all:0,unfiled:0,folders:{}}};

const bookmarkReducer=(state,action)=> {
  switch (action.type) {
    case 'SET_LOADING': return {...state,isLoading:action.payload};
    case 'SET_ERROR': return {...state,error:action.payload};
    case 'SET_BOOKMARKS': return {...state,bookmarks:action.payload};
    case 'SET_FOLDERS': return {...state,folders:action.payload};
    case 'SET_BOOKMARK_COUNTS': return {...state,bookmarkCounts:{...state.bookmarkCounts,...action.payload}};
    case 'ADD_BOOKMARK': return {...state,bookmarks:[...state.bookmarks,action.payload]};
    case 'UPSERT_BOOKMARK': {
      const exists=state.bookmarks.some(bookmark=> bookmark.id===action.payload.id);
      return {...state,bookmarks:exists ? state.bookmarks.map(bookmark=> bookmark.id===action.payload.id ? action.payload : bookmark) : [...state.bookmarks,action.payload]};
    }
    case 'REPLACE_BOOKMARK_ID': return {...state,bookmarks:replaceRecordId(state.bookmarks,action.payload.oldId,action.payload.bookmark)};
    case 'RESTORE_BOOKMARK': {
      const without=state.bookmarks.filter(bookmark=> bookmark.id!==action.payload.bookmark.id);
      const index=Math.max(0,Math.min(action.payload.index,without.length));
      const next=[...without];
      next.splice(index,0,action.payload.bookmark);
      return {...state,bookmarks:next};
    }
    case 'MOVE_BOOKMARK_OPTIMISTIC': return {...state,bookmarks:moveBookmarkInList(state.bookmarks,action.payload.id,action.payload.folderId,action.payload.destinationIndex)};
    case 'DELETE_BOOKMARK': return {...state,bookmarks:state.bookmarks.filter(bookmark=> bookmark.id!==action.payload),selectedItems:state.selectedItems.filter(id=> id!==action.payload)};
    case 'UPSERT_FOLDER': {
      const exists=state.folders.some(folder=> folder.id===action.payload.id);
      return {...state,folders:exists ? state.folders.map(folder=> folder.id===action.payload.id ? action.payload : folder) : [...state.folders,action.payload]};
    }
    case 'REPLACE_FOLDER_ID': {
      const {oldId,folder}=action.payload;
      const folders=replaceRecordId(state.folders,oldId,folder).map(item=> item.id===folder.id ? item : {...item,parent:item.parent===oldId ? folder.id : item.parent});
      return {...state,folders,bookmarks:state.bookmarks.map(bookmark=> bookmark.folderId===oldId ? {...bookmark,folderId:folder.id} : bookmark),activeFolderId:state.activeFolderId===oldId ? folder.id : state.activeFolderId};
    }
    case 'DELETE_FOLDER': return {...state,folders:state.folders.filter(folder=> folder.id!==action.payload),activeFolderId:state.activeFolderId===action.payload ? 'ALL' : state.activeFolderId};
    case 'REMOVE_FOLDERS': return {...state,folders:state.folders.filter(folder=> !action.payload.includes(folder.id)),activeFolderId:action.payload.includes(state.activeFolderId) ? 'ALL' : state.activeFolderId};
    case 'SET_SELECTED_ITEMS': return {...state,selectedItems:action.payload};
    case 'SET_SELECTED_FOLDERS': return {...state,selectedFolders:action.payload};
    case 'SET_SEARCH_QUERY': return {...state,searchQuery:action.payload};
    case 'SET_SEARCH_RESULTS': return {...state,searchResults:action.payload};
    case 'SET_SEARCH_SCOPE': return {...state,searchScope:action.payload};
    case 'SET_ACTIVE_FOLDER': return {...state,activeFolderId:action.payload};
    case 'CLEAR_UNDO_STACK': return {...state,undoStack:[]};
    default: return state;
  }
};

export const BookmarkProvider=({children})=> {
  const [state,dispatch]=useReducer(bookmarkReducer,initialState);
  const [highlightId,setHighlightId]=useState(null);
  const [isAddModalOpen,setIsAddModalOpen]=useState(false);
  const [editingBookmark,setEditingBookmark]=useState(null);
  const [syncStatus,setSyncStatus]=useState('loading');
  const [pendingChanges,setPendingChanges]=useState(0);
  const [lastSyncedAt,setLastSyncedAt]=useState(null);
  const [isOnline,setIsOnline]=useState(()=> typeof navigator==='undefined' ? true : navigator.onLine!==false);
  const [cacheReady,setCacheReady]=useState(false);
  const pendingChangesRef=useRef(0);
  const refreshInFlightRef=useRef(false);
  const lastSuccessfulRefreshAtRef=useRef(0);
  const loadRequestIdRef=useRef(0);
  const flushingRef=useRef(false);
  const bootUserRef=useRef(null);
  const {user,isAuthenticated}=useAuth();

  const calculateLocalCounts=(bookmarks,folders)=> {
    const counts={all:bookmarks.length,unfiled:0,folders:{}};
    folders.forEach(folder=> {counts.folders[folder.id]=0;});
    bookmarks.forEach(bookmark=> {
      if (!bookmark.folderId) counts.unfiled+=1;
      else if (counts.folders[bookmark.folderId]!==undefined) counts.folders[bookmark.folderId]+=1;
    });
    return counts;
  };

  useEffect(()=> {dispatch({type:'SET_BOOKMARK_COUNTS',payload:calculateLocalCounts(state.bookmarks,state.folders)});},[state.bookmarks,state.folders]);

  useEffect(()=> {
    if (!user?.id || !cacheReady) return undefined;
    const timer=window.setTimeout(()=> saveCachedLibrary(user.id,{bookmarks:state.bookmarks,folders:state.folders}),180);
    return ()=> window.clearTimeout(timer);
  },[user?.id,cacheReady,state.bookmarks,state.folders]);

  const updatePendingCount=async ()=> {
    if (!user?.id) {pendingChangesRef.current=0;setPendingChanges(0);return 0;}
    const count=await getPendingMutationCount(user.id);
    pendingChangesRef.current=count;
    setPendingChanges(count);
    return count;
  };

  const markSynced=()=> {
    const timestamp=Date.now();
    lastSuccessfulRefreshAtRef.current=timestamp;
    setLastSyncedAt(timestamp);
    setSyncStatus(pendingChangesRef.current>0 ? 'pending' : 'synced');
  };

  const loadData=async (silent=false,force=false)=> {
    if (!isAuthenticated || !user?.id) return;
    const now=Date.now();
    if (silent && !force && lastSuccessfulRefreshAtRef.current && now-lastSuccessfulRefreshAtRef.current<BACKGROUND_REFRESH_MIN_INTERVAL_MS) return;
    const currentRequestId=++loadRequestIdRef.current;
    if (!silent && state.bookmarks.length===0) dispatch({type:'SET_LOADING',payload:true});
    if (typeof navigator==='undefined' || navigator.onLine!==false) setSyncStatus('syncing');
    try {
      const [bookmarks,folders]=await Promise.all([dataService.getAllBookmarks({silent:true}),dataService.listFolders({silent:true})]);
      if (currentRequestId!==loadRequestIdRef.current) return;
      if (Array.isArray(bookmarks) && Array.isArray(folders)) {
        dispatch({type:'SET_BOOKMARKS',payload:bookmarks});
        dispatch({type:'SET_FOLDERS',payload:folders});
        await saveCachedLibrary(user.id,{bookmarks,folders});
        markSynced();
      }
    } catch (error) {
      if (currentRequestId!==loadRequestIdRef.current) return;
      if (dataService.isOfflineError(error) || error?.isOffline) {setIsOnline(false);setSyncStatus('offline');}
      else {if (!silent) dispatch({type:'SET_ERROR',payload:error.message});setSyncStatus('error');}
    } finally {
      if (currentRequestId===loadRequestIdRef.current) dispatch({type:'SET_LOADING',payload:false});
    }
  };

  const resolveQueuedId=(id,idMap)=> idMap.get(id) || id;

  const flushPendingMutations=async ({refreshAfter=true}={})=> {
    if (!isAuthenticated || !user?.id || flushingRef.current) return false;
    if (typeof navigator!=='undefined' && navigator.onLine===false) {setSyncStatus('offline');return false;}
    flushingRef.current=true;
    setSyncStatus('syncing');
    const idMap=new Map();
    let allSucceeded=true;
    try {
      const mutations=await getQueuedMutations(user.id);
      for (const mutation of mutations) {
        if ((mutation.queueVersion || 1)!==QUEUE_SCHEMA_VERSION) {
          allSucceeded=false;
          await updateQueuedMutation(mutation.id,{lastError:'Pending change was created by an incompatible app version.',lastAttemptAt:Date.now()});
          setSyncStatus('error');
          dataService.showToast('A pending offline change needs a newer compatibility migration before it can sync. It has been kept safely on this device.','warning',6000);
          break;
        }
        try {
          const targetId=resolveQueuedId(mutation.targetId,idMap);
          const payload={...(mutation.payload || {})};
          if (payload.folderId) payload.folderId=resolveQueuedId(payload.folderId,idMap);
          if (payload.parent) payload.parent=resolveQueuedId(payload.parent,idMap);

          if (mutation.entity==='bookmark') {
            if (mutation.operation==='create') {
              const created=await dataService.createBookmark(payload,{silent:true});
              idMap.set(mutation.targetId,created.id);
              await remapQueuedEntityId(user.id,'bookmark',mutation.targetId,created.id);
              dispatch({type:'REPLACE_BOOKMARK_ID',payload:{oldId:mutation.targetId,bookmark:created}});
            } else if (mutation.operation==='update') {
              if (isTemporaryId(targetId)) throw new Error('Pending bookmark creation has not been resolved yet');
              dispatch({type:'UPSERT_BOOKMARK',payload:await dataService.updateBookmark(targetId,payload,{silent:true})});
            } else if (mutation.operation==='delete') {
              if (!isTemporaryId(targetId)) await dataService.deleteBookmark(targetId,{silent:true});
              dispatch({type:'DELETE_BOOKMARK',payload:targetId});
            }
          } else if (mutation.entity==='folder') {
            if (mutation.operation==='create') {
              const created=await dataService.createFolder(payload.name,payload.parent || null,payload.sortOrder || 0,{silent:true});
              idMap.set(mutation.targetId,created.id);
              await remapQueuedEntityId(user.id,'folder',mutation.targetId,created.id);
              dispatch({type:'REPLACE_FOLDER_ID',payload:{oldId:mutation.targetId,folder:created}});
            } else if (mutation.operation==='update') {
              if (isTemporaryId(targetId)) throw new Error('Pending folder creation has not been resolved yet');
              dispatch({type:'UPSERT_FOLDER',payload:await dataService.updateFolder(targetId,payload,{silent:true})});
            } else if (mutation.operation==='delete') {
              if (!isTemporaryId(targetId)) await dataService.deleteFolder(targetId,{silent:true});
              dispatch({type:'DELETE_FOLDER',payload:targetId});
            }
          } else if (mutation.entity==='setting' && mutation.operation==='set') {
            await dataService.setSetting(payload.key,payload.value,{silent:true});
          }
          await removeQueuedMutation(mutation.id,user.id);
        } catch (error) {
          allSucceeded=false;
          if (dataService.isOfflineError(error) || error?.isOffline) {setIsOnline(false);setSyncStatus('offline');break;}
          await updateQueuedMutation(mutation.id,{attempts:(mutation.attempts || 0)+1,lastError:error.message,lastAttemptAt:Date.now()});
          setSyncStatus('error');
          console.error('Pending change needs attention:',mutation,error);
          break;
        }
      }
      await updatePendingCount();
      if (allSucceeded) {
        setIsOnline(true);
        if (refreshAfter) await loadData(true,true);
        else markSynced();
      }
      return allSucceeded;
    } finally {flushingRef.current=false;}
  };

  useEffect(()=> {
    if (!isAuthenticated || !user?.id) {
      bootUserRef.current=null;setCacheReady(false);pendingChangesRef.current=0;setPendingChanges(0);setSyncStatus('loading');lastSuccessfulRefreshAtRef.current=0;return undefined;
    }
    if (bootUserRef.current===user.id) return undefined;
    bootUserRef.current=user.id;
    let cancelled=false;
    const bootstrap=async ()=> {
      const cached=await getCachedLibrary(user.id);
      if (cancelled) return;
      if (cached) {dispatch({type:'SET_BOOKMARKS',payload:cached.bookmarks || []});dispatch({type:'SET_FOLDERS',payload:cached.folders || []});setLastSyncedAt(cached.updatedAt || null);}
      setCacheReady(true);
      const pending=await updatePendingCount();
      if (cancelled) return;
      if (typeof navigator!=='undefined' && navigator.onLine===false) {setIsOnline(false);setSyncStatus('offline');return;}
      setIsOnline(true);
      if (pending>0) await flushPendingMutations({refreshAfter:false});
      if (!cancelled) await loadData(Boolean(cached),true);
    };
    bootstrap();
    return ()=> {cancelled=true;};
  },[isAuthenticated,user?.id]);

  useEffect(()=> {
    if (!isAuthenticated || !user?.id) return undefined;
    const handleQueueChanged=(event)=> {
      if (event.detail?.userId && event.detail.userId!==user.id) return;
      updatePendingCount().then(count=> {
        if (count>0) setSyncStatus((typeof navigator!=='undefined' && navigator.onLine===false) ? 'offline' : 'pending');
        else if (typeof navigator==='undefined' || navigator.onLine!==false) setSyncStatus(current=> current==='error' ? current : 'synced');
      });
    };
    window.addEventListener('bmp:queue-changed',handleQueueChanged);
    return ()=> window.removeEventListener('bmp:queue-changed',handleQueueChanged);
  },[isAuthenticated,user?.id]);

  useEffect(()=> {
    if (!isAuthenticated || !user?.id) return undefined;
    let unsubscribe=null;
    let cancelled=false;
    dataService.subscribeToLibrary({
      onBookmark:({action,record})=> {if (cancelled) return;if (action==='delete') dispatch({type:'DELETE_BOOKMARK',payload:record.id});else dispatch({type:'UPSERT_BOOKMARK',payload:record});markSynced();},
      onFolder:({action,record})=> {if (cancelled) return;if (action==='delete') dispatch({type:'DELETE_FOLDER',payload:record.id});else dispatch({type:'UPSERT_FOLDER',payload:record});markSynced();}
    }).then(cleanup=> {if (cancelled) cleanup?.();else unsubscribe=cleanup;}).catch(error=> console.warn('Realtime library subscription unavailable; focus refresh remains active.',error));
    return ()=> {cancelled=true;unsubscribe?.();};
  },[isAuthenticated,user?.id]);

  const refreshAllData=async (silent=true,force=false)=> {
    if (!isAuthenticated || refreshInFlightRef.current) return;
    refreshInFlightRef.current=true;
    try {await loadData(silent,force);} finally {refreshInFlightRef.current=false;}
  };

  useEffect(()=> {
    if (!isAuthenticated || !user?.id) return undefined;
    const handleOnline=async ()=> {setIsOnline(true);await flushPendingMutations({refreshAfter:false});await refreshAllData(true,true);};
    const handleOffline=()=> {setIsOnline(false);setSyncStatus('offline');};
    const handleFocusRefresh=()=> {if (navigator.onLine!==false) refreshAllData(true,false);};
    const handleVisibilityRefresh=()=> {if (document.visibilityState==='visible' && navigator.onLine!==false) refreshAllData(true,false);};
    window.addEventListener('online',handleOnline);window.addEventListener('offline',handleOffline);window.addEventListener('focus',handleFocusRefresh);document.addEventListener('visibilitychange',handleVisibilityRefresh);
    return ()=> {window.removeEventListener('online',handleOnline);window.removeEventListener('offline',handleOffline);window.removeEventListener('focus',handleFocusRefresh);document.removeEventListener('visibilitychange',handleVisibilityRefresh);};
  },[isAuthenticated,user?.id]);

  const triggerHighlight=(id)=> {setHighlightId(id);window.setTimeout(()=> setHighlightId(null),1200);};

  const queueOfflineChange=async (mutation,message='Saved offline — it will sync automatically')=> {
    await queueMutation(user.id,mutation);
    const count=await updatePendingCount();
    setSyncStatus((typeof navigator!=='undefined' && navigator.onLine===false) ? 'offline' : 'pending');
    dataService.showToast(message,'warning');
    return count;
  };

  const addBookmark=async (bookmarkData)=> {
    const tempId=`temp-${uuidv4()}`;
    const optimistic={...bookmarkData,id:tempId,dateAdded:nowDate(),dateModified:nowDate(),_pending:true};
    dispatch({type:'ADD_BOOKMARK',payload:optimistic});triggerHighlight(tempId);
    if (navigator.onLine===false) {await queueOfflineChange({entity:'bookmark',operation:'create',targetId:tempId,payload:bookmarkData});return optimistic;}
    try {
      const real=await dataService.createBookmark(bookmarkData,{silent:true});
      dispatch({type:'REPLACE_BOOKMARK_ID',payload:{oldId:tempId,bookmark:real}});markSynced();triggerHighlight(real.id);dataService.showToast('Bookmark saved','success');return real;
    } catch (error) {
      if (dataService.isOfflineError(error) || error?.isOffline) {await queueOfflineChange({entity:'bookmark',operation:'create',targetId:tempId,payload:bookmarkData});return optimistic;}
      dispatch({type:'DELETE_BOOKMARK',payload:tempId});throw error;
    }
  };

  const updateBookmark=async (id,updates)=> {
    const original=state.bookmarks.find(bookmark=> bookmark.id===id);
    if (!original) throw new Error('Bookmark not found');
    const optimistic={...original,...updates,id,dateModified:nowDate(),_pending:true};
    dispatch({type:'UPSERT_BOOKMARK',payload:optimistic});
    if (navigator.onLine===false || isTemporaryId(id)) {await queueOfflineChange({entity:'bookmark',operation:'update',targetId:id,payload:updates});return optimistic;}
    try {const updated=await dataService.updateBookmark(id,updates,{silent:true});dispatch({type:'UPSERT_BOOKMARK',payload:updated});markSynced();return updated;}
    catch (error) {if (dataService.isOfflineError(error) || error?.isOffline) {await queueOfflineChange({entity:'bookmark',operation:'update',targetId:id,payload:updates});return optimistic;}dispatch({type:'UPSERT_BOOKMARK',payload:original});throw error;}
  };

  const moveBookmark=async (id,folderId,destinationIndex)=> {
    const originalIndex=state.bookmarks.findIndex(bookmark=> bookmark.id===id);
    if (originalIndex===-1) throw new Error('Bookmark not found');
    const original=state.bookmarks[originalIndex];
    const targetFolderId=normaliseFolderId(folderId);
    const payload={folderId:targetFolderId,sortOrder:Math.max(0,Number(destinationIndex) || 0)};
    dispatch({type:'MOVE_BOOKMARK_OPTIMISTIC',payload:{id,folderId:targetFolderId,destinationIndex}});
    if (navigator.onLine===false || isTemporaryId(id) || isTemporaryId(targetFolderId)) {await queueOfflineChange({entity:'bookmark',operation:'update',targetId:id,payload});return {...original,...payload,_pending:true,dateModified:nowDate()};}
    try {const updated=await dataService.updateBookmark(id,payload,{silent:true});dispatch({type:'UPSERT_BOOKMARK',payload:updated});markSynced();return updated;}
    catch (error) {if (dataService.isOfflineError(error) || error?.isOffline) {await queueOfflineChange({entity:'bookmark',operation:'update',targetId:id,payload});return {...original,...payload,_pending:true,dateModified:nowDate()};}dispatch({type:'RESTORE_BOOKMARK',payload:{bookmark:original,index:originalIndex}});throw error;}
  };

  const deleteBookmark=async (id)=> {
    const originalIndex=state.bookmarks.findIndex(bookmark=> bookmark.id===id);
    const original=state.bookmarks[originalIndex];
    if (!original) return;
    dispatch({type:'DELETE_BOOKMARK',payload:id});
    if (isTemporaryId(id)) {await cancelQueuedEntity(user.id,'bookmark',id);await updatePendingCount();return;}
    if (navigator.onLine===false) {await queueOfflineChange({entity:'bookmark',operation:'delete',targetId:id,payload:{}});return;}
    try {await dataService.deleteBookmark(id,{silent:true});markSynced();}
    catch (error) {if (dataService.isOfflineError(error) || error?.isOffline) {await queueOfflineChange({entity:'bookmark',operation:'delete',targetId:id,payload:{}});return;}dispatch({type:'RESTORE_BOOKMARK',payload:{bookmark:original,index:originalIndex}});throw error;}
  };

  const addFolder=async (folderData)=> {
    const tempId=`temp-${uuidv4()}`;
    const optimistic={...folderData,id:tempId,parent:folderData.parent || null,dateAdded:nowDate(),dateModified:nowDate(),_pending:true};
    dispatch({type:'UPSERT_FOLDER',payload:optimistic});triggerHighlight(tempId);
    const payload={name:folderData.name,parent:folderData.parent || null,sortOrder:folderData.sortOrder || 0};
    if (navigator.onLine===false || isTemporaryId(payload.parent)) {await queueOfflineChange({entity:'folder',operation:'create',targetId:tempId,payload});return optimistic;}
    try {const real=await dataService.createFolder(payload.name,payload.parent,payload.sortOrder,{silent:true});dispatch({type:'REPLACE_FOLDER_ID',payload:{oldId:tempId,folder:real}});markSynced();triggerHighlight(real.id);return real;}
    catch (error) {if (dataService.isOfflineError(error) || error?.isOffline) {await queueOfflineChange({entity:'folder',operation:'create',targetId:tempId,payload});return optimistic;}dispatch({type:'DELETE_FOLDER',payload:tempId});throw error;}
  };

  const updateFolder=async (id,updates)=> {
    const original=state.folders.find(folder=> folder.id===id);
    if (!original) throw new Error('Folder not found');
    const optimistic={...original,...updates,id,dateModified:nowDate(),_pending:true};
    dispatch({type:'UPSERT_FOLDER',payload:optimistic});
    if (navigator.onLine===false || isTemporaryId(id) || isTemporaryId(updates.parent)) {await queueOfflineChange({entity:'folder',operation:'update',targetId:id,payload:updates});return optimistic;}
    try {const updated=await dataService.updateFolder(id,updates,{silent:true});dispatch({type:'UPSERT_FOLDER',payload:updated});markSynced();return updated;}
    catch (error) {if (dataService.isOfflineError(error) || error?.isOffline) {await queueOfflineChange({entity:'folder',operation:'update',targetId:id,payload:updates});return optimistic;}dispatch({type:'UPSERT_FOLDER',payload:original});throw error;}
  };

  const getDescendantFolders=(folderId)=> {
    const descendants=[];
    const directChildren=state.folders.filter(folder=> folder.parent===folderId);
    for (const child of directChildren) {descendants.push(child.id);descendants.push(...getDescendantFolders(child.id));}
    return descendants;
  };

  const deleteFolder=async (id,moveBookmarksToUnfiled=true)=> {
    const folderIds=[id,...getDescendantFolders(id)];
    const affected=state.bookmarks.filter(bookmark=> folderIds.includes(bookmark.folderId));
    if (moveBookmarksToUnfiled) for (const bookmark of affected) await updateBookmark(bookmark.id,{folderId:null});
    dispatch({type:'REMOVE_FOLDERS',payload:folderIds});
    for (const folderId of folderIds) {
      if (isTemporaryId(folderId)) {await cancelQueuedEntity(user.id,'folder',folderId);continue;}
      if (navigator.onLine===false) {await queueOfflineChange({entity:'folder',operation:'delete',targetId:folderId,payload:{}},'Folder change saved offline');continue;}
      try {await dataService.deleteFolder(folderId,{silent:true});}
      catch (error) {if (dataService.isOfflineError(error) || error?.isOffline) await queueOfflineChange({entity:'folder',operation:'delete',targetId:folderId,payload:{}},'Folder change saved offline');else {await loadData(true,true);throw error;}}
    }
    await updatePendingCount();
    if (navigator.onLine!==false) markSynced();
  };

  const bulkDelete=async (ids)=> {const unique=[...new Set(ids || [])];for (const id of unique) await deleteBookmark(id);dispatch({type:'SET_SELECTED_ITEMS',payload:[]});return unique.length;};
  const bulkMoveBookmarks=async (ids,folderId)=> {const unique=[...new Set(ids || [])];const baseIndex=state.bookmarks.filter(bookmark=> normaliseFolderId(bookmark.folderId)===normaliseFolderId(folderId) && !unique.includes(bookmark.id)).length;for (let index=0;index<unique.length;index+=1) await moveBookmark(unique[index],folderId,baseIndex+index);dispatch({type:'SET_SELECTED_ITEMS',payload:[]});return unique.length;};
  const bulkAddTags=async (ids,tags)=> {const additions=dataService.normaliseTags(tags);const unique=[...new Set(ids || [])];for (const id of unique) {const bookmark=state.bookmarks.find(item=> item.id===id);if (!bookmark) continue;const nextTags=[...bookmark.tags];additions.forEach(tag=> {if (!nextTags.some(existing=> existing.toLowerCase()===tag.toLowerCase())) nextTags.push(tag);});await updateBookmark(id,{tags:nextTags});}return unique.length;};

  const importLibrary=async ({folders=[],bookmarks=[]}={})=> {
    if (navigator.onLine===false) throw new Error('Connect to the internet before restoring a library backup.');
    const folderMap=new Map();let foldersCreated=0;let bookmarksCreated=0;let duplicatesSkipped=0;
    const existingUrls=new Set(state.bookmarks.map(bookmark=> normaliseUrlKey(bookmark.url)));
    const pendingFolders=[...(folders || [])];let safety=0;
    while (pendingFolders.length && safety<Math.max(10,pendingFolders.length*3)) {
      safety+=1;let progressed=false;
      for (let i=pendingFolders.length-1;i>=0;i-=1) {
        const folder=pendingFolders[i];const oldParent=folder.parent ?? folder.parentId ?? null;
        if (oldParent && !folderMap.has(oldParent) && pendingFolders.some(item=> item.id===oldParent)) continue;
        const parent=oldParent ? (folderMap.get(oldParent) || null) : null;
        const created=await dataService.createFolder(folder.name || 'Imported Folder',parent,folder.sortOrder || 0,{silent:true});folderMap.set(folder.id,created.id);foldersCreated+=1;pendingFolders.splice(i,1);progressed=true;
      }
      if (!progressed) break;
    }
    for (const folder of pendingFolders) {const created=await dataService.createFolder(folder.name || 'Imported Folder',null,folder.sortOrder || 0,{silent:true});folderMap.set(folder.id,created.id);foldersCreated+=1;}
    for (const bookmark of bookmarks || []) {
      const url=String(bookmark.url || '').trim();if (!/^https?:\/\//i.test(url)) continue;
      const key=normaliseUrlKey(url);if (existingUrls.has(key)) {duplicatesSkipped+=1;continue;}
      const folderId=bookmark.folderId ? (folderMap.get(bookmark.folderId) || null) : null;
      await dataService.createBookmark({...bookmark,id:undefined,folderId,faviconfile:null},{silent:true});existingUrls.add(key);bookmarksCreated+=1;
    }
    await loadData(true,true);dataService.showToast(`Restore complete: ${bookmarksCreated} bookmarks added`,'success',5000);return {foldersCreated,bookmarksCreated,duplicatesSkipped};
  };

  const getBookmarksInFolderTree=(folderId)=> {const folderIds=[folderId,...getDescendantFolders(folderId)];return state.bookmarks.filter(bookmark=> folderIds.includes(bookmark.folderId));};
  const getBookmarkCount=(folderId)=> folderId==='ALL' ? state.bookmarkCounts.all : folderId==='UNFILED' ? state.bookmarkCounts.unfiled : state.bookmarkCounts.folders[folderId] || 0;
  const loadBookmarkCounts=async ()=> {const counts=calculateLocalCounts(state.bookmarks,state.folders);dispatch({type:'SET_BOOKMARK_COUNTS',payload:counts});return counts;};

  const value={...state,highlightId,currentFolder:state.activeFolderId,setCurrentFolder:(id)=> dispatch({type:'SET_ACTIVE_FOLDER',payload:id}),addBookmark,updateBookmark,moveBookmark,deleteBookmark,bulkDelete,bulkMoveBookmarks,bulkAddTags,addFolder,updateFolder,deleteFolder,importLibrary,loadData,refreshAllData,retryPendingChanges:()=> flushPendingMutations({refreshAfter:true}),getBookmarkCount,getDescendantFolders,getBookmarksInFolderTree,loadBookmarkCounts,isAddModalOpen,setIsAddModalOpen,editingBookmark,setEditingBookmark,setSelectedItems:(items)=> dispatch({type:'SET_SELECTED_ITEMS',payload:items}),setSelectedFolders:(folders)=> dispatch({type:'SET_SELECTED_FOLDERS',payload:folders}),search:(query)=> dispatch({type:'SET_SEARCH_QUERY',payload:query}),setSearchScope:(scope)=> dispatch({type:'SET_SEARCH_SCOPE',payload:scope}),undo:()=> {},syncStatus,pendingChanges,lastSyncedAt,isOnline,cacheReady};
  return <BookmarkContext.Provider value={value}>{children}</BookmarkContext.Provider>;
};